/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import ExcelJS from 'exceljs';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PostService from '../../../services/postService';
import { useCompany } from '../../../provider/companyProvider';
import { formatPrice, getPostTypePdf } from '../../../utils';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { fileApiRoute } from '../../../services/networkService';
import dayjs from 'dayjs';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';

// ── Palette ──────────────────────────────────────────────────────
const C_TEAL       = 'FF1E7A6E';
const C_TEAL_LIGHT = 'FFE8F5F3';
const C_WHITE      = 'FFFFFFFF';
const C_BLACK      = 'FF111111';
const C_GRAY       = 'FF555555';
const C_BORDER     = 'FFCCCCCC';
const C_STRIPE     = 'FFF0FAF8'; // very light teal stripe

// ── Reusable border presets ───────────────────────────────────────
const THIN_BORDER = {
    top:    { style: 'thin',   color: { argb: C_BORDER } },
    left:   { style: 'thin',   color: { argb: C_BORDER } },
    bottom: { style: 'thin',   color: { argb: C_BORDER } },
    right:  { style: 'thin',   color: { argb: C_BORDER } },
};
const THICK_TEAL_BORDER = {
    top:    { style: 'medium', color: { argb: C_TEAL } },
    left:   { style: 'medium', color: { argb: C_TEAL } },
    bottom: { style: 'medium', color: { argb: C_TEAL } },
    right:  { style: 'medium', color: { argb: C_TEAL } },
};

const TOTAL_COLS = 15; // A–O

// ─────────────────────────────────────────────────────────────────
//  Helper: add a section-title bar (full width)
// ─────────────────────────────────────────────────────────────────
function sectionTitle(ws, label) {
    const r = ws.addRow([]);
    r.height = 26;
    ws.mergeCells(r.number, 1, r.number, TOTAL_COLS);
    const c = ws.getCell(r.number, 1);
    c.value = label;
    c.font  = { bold: true, size: 12, color: { argb: C_WHITE } };
    c.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: C_TEAL } };
    c.alignment = { vertical: 'middle', horizontal: 'center' };
    c.border = THICK_TEAL_BORDER;
    return r.number + 1;
}

// ─────────────────────────────────────────────────────────────────
//  Helper: add one key-value row (full width)
//  KEY occupies right side (cols 1-4 → right in RTL)
//  VALUE occupies left side (cols 5-12 → left in RTL)
// ─────────────────────────────────────────────────────────────────
function kvRow(ws, key, value, stripe = false) {
    const r = ws.addRow([]);
    r.height = 22;
    const rn = r.number;

    const bg = stripe ? C_TEAL_LIGHT : C_WHITE;

    // Key cell — cols 1-4 (right side in RTL)
    ws.mergeCells(rn, 1, rn, 4);
    const kc = ws.getCell(rn, 1);
    kc.value = key;
    kc.font  = { bold: true, size: 10, color: { argb: C_TEAL } };
    kc.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
    kc.alignment = { vertical: 'middle', horizontal: 'center' };
    kc.border = THIN_BORDER;

    // Value cell — cols 5-12 (left side in RTL)
    ws.mergeCells(rn, 5, rn, TOTAL_COLS);
    const vc = ws.getCell(rn, 5);
    vc.value = value;
    vc.font  = { size: 10, color: { argb: C_BLACK } };
    vc.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: C_WHITE } };
    vc.alignment = { vertical: 'middle', horizontal: 'center' };
    vc.border = THIN_BORDER;
    if (typeof value === 'number') vc.numFmt = '#,##0';
}

// ─────────────────────────────────────────────────────────────────
//  Helper: spacer
// ─────────────────────────────────────────────────────────────────
function spacer(ws, h = 8) {
    const r = ws.addRow([]);
    r.height = h;
}

// ═════════════════════════════════════════════════════════════════
//  COMPONENT
// ═════════════════════════════════════════════════════════════════
const ExcelPostList = ({ data, type }) => {
    const { i18n, t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const { business } = useCompany();
    const isRTL = i18n.language !== 'en';

    const handleExport = async () => {
        setIsLoading(true);

        const filters = data && {
            name:         data.name,
            streetId:     data.streetId,
            cityId:       data.cityId,
            driverId:     data.driverId,
            branchId:     data.branch && data.branch.id,
            pageId:       data.pageId,
            postStatusId: data.postStatusId,
            type:         data.type,
            isPagePaid:   data.isPagePaid,
            isDriverPaid: data.isDriverPaid,
            ...data.time,
            ...data.sort,
            ids:          data.ids,
        };

        try {
            const response = await PostService.fetchAll(filters);
            const posts    = Array.isArray(response) ? response : (response.posts || []);

            // ── Workbook ────────────────────────────────────────
            const wb = new ExcelJS.Workbook();
            wb.creator = business?.name || 'Export';

            const sheetTitle = type === 'page' ? t('pageReport') : t('driverReport');

            const ws = wb.addWorksheet(sheetTitle, {
                views: [{ rightToLeft: isRTL, showGridLines: false }],
                pageSetup: {
                    paperSize:    9,        // A4
                    orientation:  'landscape',
                    margins: { left: 0.5, right: 0.5, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 },
                },
            });

            // ── Column widths (A–M) ─────────────────────────────
            ws.columns = [
                { width: 12 }, // A - Seq
                { width: 15 }, // B - ID
                { width: 22 }, // C - Date
                { width: 20 }, // D - Driver
                { width: 35 }, // E - Address
                { width: 20 }, // F - Rec Phone
                { width: 20 }, // G - Rec Name
                { width: 15 }, // H - Type
                { width: 18 }, // I - Price
                { width: 20 }, // J - Supervisor
                { width: 25 }, // K - Note
                { width: 20 }, // L - Page Name
                { width: 20 }, // M - Page Phone
                { width: 20 }, // N - QR Code (wider for centering)
                { width: 35 }, // O - Barcode (wider for centering)
            ];

            // ── Fetch logo ───────────────────────────────────────
            let logoId = null;
            if (business?.logo) {
                try {
                    const res    = await fetch(`${fileApiRoute}/${business.logo}`);
                    const blob   = await res.blob();
                    const base64 = await new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result.split(',')[1]);
                        reader.readAsDataURL(blob);
                    });
                    logoId = wb.addImage({
                        base64,
                        extension: business.logo.split('.').pop().toLowerCase() === 'png' ? 'png' : 'jpeg',
                    });
                } catch (e) {
                    console.error('Logo load error:', e);
                }
            }

            // ════════════════════════════════════════════════════
            //  BLOCK 1 — PAGE HEADER (ALIGNED)
            // ════════════════════════════════════════════════════

            // Top teal accent bar removed

            const alignHorizontal = isRTL ? 'right' : 'left';

            spacer(ws, 15); // Top spacing
            
            // We create 3 rows for the name, phone, and date text
            const r1 = ws.addRow([]);
            r1.height = 30; 
            const r2 = ws.addRow([]);
            r2.height = 24; 
            const r3 = ws.addRow([]);
            r3.height = 24; 

            // Text section — Cols 1-6 (Visually right side in RTL, left side in LTR)
            ws.mergeCells(r1.number, 1, r1.number, 6);
            const nameCell = ws.getCell(r1.number, 1);
            nameCell.value = business?.name || '';
            nameCell.font = { bold: true, size: 22, color: { argb: C_TEAL } };
            nameCell.alignment = { vertical: 'middle', horizontal: alignHorizontal, indent: 2 };

            ws.mergeCells(r2.number, 1, r2.number, 6);
            const phoneCell = ws.getCell(r2.number, 1);
            phoneCell.value = business?.phoneOne || '';
            phoneCell.font = { size: 12, color: { argb: C_GRAY } };
            phoneCell.alignment = { vertical: 'middle', horizontal: alignHorizontal, indent: 2 };

            ws.mergeCells(r3.number, 1, r3.number, 6);
            const dateCell = ws.getCell(r3.number, 1);
            dateCell.value = dayjs().format('DD / MM / YYYY - HH:mm');
            dateCell.font = { size: 12, color: { argb: C_GRAY } };
            dateCell.alignment = { vertical: 'middle', horizontal: alignHorizontal, indent: 2 };

            // Logo section — Cols 13-15 (Far left in RTL)
            if (logoId !== null) {
                ws.mergeCells(r1.number, 13, r3.number, TOTAL_COLS);
                ws.addImage(logoId, {
                    tl: { col: 13.5, row: r1.number - 0.5 },
                    ext: { width: 100, height: 65 }
                });
            }

            // Bottom teal accent bar removed

            spacer(ws, 15); // Space before table starts

            // ════════════════════════════════════════════════════
            //  BLOCK 3 — TABLE DATA
            // ════════════════════════════════════════════════════

            // ── Table Header ──────────────────────────────────────────
            const headerRow = ws.addRow([
                t('number') || 'زنجیرە',
                'ID',
                t('date') || 'بەروار',
                t('driver'),
                t('address'),
                t('receiverPhone'),
                t('receiverName'),
                t('postType'),
                t('totalPrice'),
                t('supervisor'),
                t('note'),
                t('pageName'),
                t('pagePhone'),
                t('qrCode') || 'QR Code',
                t('barcode') || 'Barcode',
            ]);
            headerRow.height = 30;
            headerRow.eachCell((cell) => {
                cell.font = { bold: true, size: 11, color: { argb: C_WHITE } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C_TEAL } };
                cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
                cell.border = THIN_BORDER;
            });

            // ── Table Rows ────────────────────────────────────────────
            for (let idx = 0; idx < posts.length; idx++) {
                const post = posts[idx];
                const sequence = idx + 1;
                const address = [post.receiverAddress, post.street?.name, post.street?.city?.name].filter(Boolean).join(', ') || '-';
                const phones = [post.receiverPhone, post.receiverPhoneTwo].filter(Boolean).join(', ') || '-';
                const postType = post.postType?.name || getPostTypePdf(post.type, { new: t('new'), change: t('change') }) || '-';
                const date = post.createdAt ? dayjs(post.createdAt).format('DD/MM/YYYY HH:mm') : '-';

                const row = ws.addRow([
                    sequence,
                    post.id,
                    date,
                    post.driver?.name || '-',
                    address,
                    phones,
                    post.receiverName || '-',
                    postType,
                    post.totalPrice ?? 0,
                    post.driver?.supervisor?.name || '-',
                    post.note || '-',
                    post.page?.name || '-',
                    post.page?.phone || '-',
                    '', // Placeholder for QR
                    '', // Placeholder for Barcode
                ]);

                row.height = 60; // 60pt ≈ 80px for image rows
                row.eachCell((cell, colNumber) => {
                    cell.font = { size: 10, color: { argb: C_BLACK } };
                    // readingOrder: 2 = RTL, 1 = LTR — ensures center alignment works correctly in RTL sheets
                    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true, readingOrder: isRTL ? 2 : 1 };
                    cell.border = THIN_BORDER;

                    if (colNumber === 9 && typeof cell.value === 'number') {
                        cell.numFmt = '#,##0';
                    }
                });

                // Add QR Code
                // Draw onto a canvas exactly the size of the cell to avoid ExcelJS RTL fractional offset bugs
                try {
                    const qrBase64 = await QRCode.toDataURL(post.id.toString(), { margin: 1, width: 65 });
                    const img = new Image();
                    img.src = qrBase64;
                    await new Promise((resolve) => {
                        img.onload = resolve;
                        img.onerror = resolve;
                    });
                    
                    const canvas = document.createElement('canvas');
                    // Col N is width 20 (~140px), row height is 60pt (~80px)
                    canvas.width = 140;
                    canvas.height = 80;
                    const ctx = canvas.getContext('2d');
                    // Left transparent so it doesn't cover Excel cell borders
                    ctx.drawImage(img, (140 - 65) / 2, (80 - 65) / 2, 65, 65);

                    const qrId = wb.addImage({
                        base64: canvas.toDataURL('image/png').split(',')[1],
                        extension: 'png',
                    });
                    ws.addImage(qrId, {
                        tl: { col: 13, row: row.number - 1 },
                        ext: { width: 140, height: 80 }
                    });
                } catch (e) { console.error('QR error', e); }

                // Add Barcode
                // Draw onto a canvas exactly the size of the cell to avoid ExcelJS RTL fractional offset bugs
                try {
                    const tempCanvas = document.createElement('canvas');
                    JsBarcode(tempCanvas, post.id.toString(), { format: "CODE128", displayValue: false, margin: 0, height: 40 });
                    
                    const canvas = document.createElement('canvas');
                    // Col O is width 35 (~245px), row height is 60pt (~80px)
                    canvas.width = 245;
                    canvas.height = 80;
                    const ctx = canvas.getContext('2d');
                    // Left transparent so it doesn't cover Excel cell borders
                    ctx.drawImage(tempCanvas, (245 - tempCanvas.width) / 2, (80 - tempCanvas.height) / 2);
                    
                    const bcId = wb.addImage({
                        base64: canvas.toDataURL('image/png').split(',')[1],
                        extension: 'png',
                    });
                    ws.addImage(bcId, {
                        tl: { col: 14, row: row.number - 1 },
                        ext: { width: 245, height: 80 }
                    });
                } catch (e) { console.error('Barcode error', e); }
            }

            // Removed bottom spacer to fix empty row with border

            // ── Download ──────────────────────────────────────
            const buffer = await wb.xlsx.writeBuffer();
            const blob   = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = URL.createObjectURL(blob);
            const a   = document.createElement('a');
            a.href    = url;
            a.download = `${type}_report_${dayjs().format('YYYY-MM-DD')}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

        } catch (err) {
            console.error('Excel export error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return isLoading ? (
        <CircularProgress size={20} />
    ) : (
        <DescriptionOutlinedIcon
            sx={{
                cursor: 'pointer',
                transition: '.2s',
                '&:hover': { color: 'primary.main', transform: 'scale(1.1)' },
            }}
            fontSize="small"
            onClick={handleExport}
            titleAccess={t('exportExcel')}
        />
    );
};


export default ExcelPostList;
